import React, { useState, useEffect } from 'react'
import {
  Container,
  Header,
  SpaceBetween,
  Cards,
  Badge,
  StatusIndicator,
  Box,
  Button,
  ColumnLayout,
  Alert,
  Modal,
  Toggle,
  FormField,
  Input
} from '@cloudscape-design/components'
import { apiService } from '../services/api'
import { useRole } from '../contexts/RoleContext'

interface DataProduct {
  id: string
  name: string
  type: 'api' | 'scraping'
  status: 'active' | 'inactive' | 'error'
  collectionStatus: 'idle' | 'processing'
  locationCount: number
  lastCollection: string
  endpoint?: string
  scrapingTargets?: number
  description: string
  collectInterval: number
}

const DataProducts: React.FC = () => {
  const { isAdmin, isProduction } = useRole()
  const canManage = isAdmin() || isProduction()
  const [dataProducts, setDataProducts] = useState<DataProduct[]>([])
  const [loading, setLoading] = useState(true)
  const [alert, setAlert] = useState<{ type: 'success' | 'error', message: string } | null>(null)
  const [editingProduct, setEditingProduct] = useState<DataProduct | null>(null)
  const [showEditModal, setShowEditModal] = useState(false)

  useEffect(() => {
    loadDataProducts()
  }, [])

  const loadDataProducts = async () => {
    setLoading(true)
    try {
      const dataProductTypes = [
        { id: 'metar', name: 'METAR Weather Data', type: 'api' as const, description: 'Aviation weather reports from airports worldwide' },
        { id: 'marine', name: 'Marine Conditions', type: 'api' as const, description: 'Marine weather observations from buoys and shore stations' },
        { id: 'trail', name: 'Snowmobile Trail Conditions', type: 'scraping' as const, description: 'Trail conditions from snowmobile club websites' },
        { id: 'nws', name: 'NWS Observations', type: 'api' as const, description: 'Weather observations from National Weather Service stations' },
        { id: 'hf_propagation', name: 'HF Radio Propagation', type: 'api' as const, description: 'D-Region Absorption Predictions for amateur radio HF bands' },
        { id: 'beach_conditions', name: 'Beach Conditions', type: 'api' as const, description: 'Beach safety forecasts from NWS including swim risk, rip currents, surf height, and UV index' }
      ]

      // Load all data products in parallel for better performance
      const products = await Promise.all(
        dataProductTypes.map(async (productType) => {
          try {
            // Fetch configuration and diagnostics in parallel
            const [configResponse, diagnosticsResponse] = await Promise.all([
              apiService.getConfiguration(productType.id),
              apiService.getDiagnostics(productType.id, true)
            ])
            
            let status: 'active' | 'inactive' | 'error' = 'inactive'
            let locationCount = 0
            let lastCollection = 'Never'
            let endpoint = ''
            let scrapingTargets = 0
            let collectInterval = 60

            if (configResponse.statusCode === 200 && configResponse.data) {
              // API returns {status: 'success', data: {configuration: {...}}}
              console.log(`[${productType.id}] Full response:`, configResponse)
              console.log(`[${productType.id}] configResponse.data:`, configResponse.data)
              
              const responseData = configResponse.data.data || configResponse.data
              console.log(`[${productType.id}] responseData:`, responseData)
              
              const config = responseData.configuration || responseData
              console.log(`[${productType.id}] config:`, config)
              console.log(`[${productType.id}] config.enabled:`, config.enabled)
              
              status = config.enabled ? 'active' : 'inactive'
              endpoint = config.api_endpoint || config.apiEndpoint || ''
              collectInterval = config.collection_interval || config.collectionInterval || 60
              
              if (productType.type === 'scraping') {
                scrapingTargets = (config.scraping_targets || config.scrapingTargets || []).length
              }
            }

            if (diagnosticsResponse.statusCode === 200 && diagnosticsResponse.data) {
              const diagnostics = diagnosticsResponse.data
              
              // Get location count from location_statistics
              if (diagnostics.location_statistics) {
                locationCount = diagnostics.location_statistics.total_locations || 0
              }
              
              // Get last collection time from diagnostics
              if (diagnostics.last_collection_time) {
                const lastCollectionTime = new Date(diagnostics.last_collection_time)
                const now = new Date()
                const diffMinutes = (now.getTime() - lastCollectionTime.getTime()) / (1000 * 60)
                
                // Format with date and time
                const dateStr = lastCollectionTime.toLocaleDateString()
                const timeStr = lastCollectionTime.toLocaleTimeString()
                
                if (diffMinutes < 60) {
                  lastCollection = `${Math.floor(diffMinutes)} minutes ago (${dateStr} ${timeStr})`
                } else if (diffMinutes < 1440) {
                  lastCollection = `${Math.floor(diffMinutes / 60)} hours ago (${dateStr} ${timeStr})`
                } else {
                  lastCollection = `${Math.floor(diffMinutes / 1440)} days ago (${dateStr} ${timeStr})`
                }
              }
            }

            // Determine collection status by checking execution locks
            let collectionStatus: 'idle' | 'processing' = 'idle'
            try {
              // Check execution locks table for active locks
              // Note: This is a simplified check - ideally the backend would provide this status
              // For now, we'll default to idle and only show processing after user triggers
              collectionStatus = 'idle'
            } catch (error) {
              console.error(`Error determining collection status for ${productType.id}:`, error)
            }

            return {
              id: productType.id,
              name: productType.name,
              type: productType.type,
              status,
              collectionStatus,
              locationCount,
              lastCollection,
              endpoint: productType.type === 'api' ? endpoint : undefined,
              scrapingTargets: productType.type === 'scraping' ? scrapingTargets : undefined,
              description: productType.description,
              collectInterval
            }
          } catch (error) {
            console.error(`Error loading data for product ${productType.id}:`, error)
            
            // Return with default/error state
            return {
              id: productType.id,
              name: productType.name,
              type: productType.type,
              status: 'error' as const,
              collectionStatus: 'idle' as const,
              locationCount: 0,
              lastCollection: 'Error',
              description: productType.description,
              collectInterval: 60
            }
          }
        })
      )

      setDataProducts(products)
    } catch (error) {
      console.error('Error loading data products:', error)
      setAlert({
        type: 'error',
        message: 'Failed to load data products. Please try again.'
      })
      
      // Set fallback data
      setDataProducts([
        {
          id: 'metar',
          name: 'METAR Weather Data',
          type: 'api',
          status: 'error',
          collectionStatus: 'idle',
          locationCount: 0,
          lastCollection: 'Error',
          description: 'Aviation weather reports from airports worldwide',
          collectInterval: 60
        },
        {
          id: 'marine',
          name: 'Marine Conditions',
          type: 'api',
          status: 'error',
          collectionStatus: 'idle',
          locationCount: 0,
          lastCollection: 'Error',
          description: 'Marine weather observations from buoys and shore stations',
          collectInterval: 60
        },
        {
          id: 'trail',
          name: 'Snowmobile Trail Conditions',
          type: 'scraping',
          status: 'error',
          collectionStatus: 'idle',
          locationCount: 0,
          lastCollection: 'Error',
          description: 'Trail conditions from snowmobile club websites',
          collectInterval: 60
        }
      ])
    } finally {
      setLoading(false)
    }
  }

  const triggerCollection = async (dataProduct: string) => {
    try {
      // Optimistically update the UI to show processing
      setDataProducts(prev => prev.map(p => 
        p.id === dataProduct ? { ...p, collectionStatus: 'processing' as const } : p
      ))

      const response = await apiService.triggerDataCollection(dataProduct)
      
      if (response.statusCode === 200) {
        setAlert({
          type: 'success',
          message: `Data collection triggered for ${dataProduct}`
        })
        // Reload data products to reflect changes
        setTimeout(() => loadDataProducts(), 2000)
      } else if (response.statusCode === 409) {
        setAlert({
          type: 'error',
          message: `Data collection is already running. Please wait for the current execution to complete.`
        })
        // Keep the processing status since it's already running
      } else {
        throw new Error(response.error || 'Failed to trigger collection')
      }
    } catch (error) {
      console.error('Error triggering data collection:', error)
      setAlert({
        type: 'error',
        message: `Failed to trigger data collection for ${dataProduct}`
      })
      // Reset to idle on error
      setDataProducts(prev => prev.map(p => 
        p.id === dataProduct ? { ...p, collectionStatus: 'idle' as const } : p
      ))
    }
  }

  const handleEditProduct = (product: DataProduct) => {
    setEditingProduct(product)
    setShowEditModal(true)
  }

  const handleSaveProduct = async () => {
    if (!editingProduct) return

    try {
      const response = await apiService.updateConfiguration(editingProduct.id, {
        enabled: editingProduct.status === 'active',
        collectionInterval: editingProduct.collectInterval
      })

      if (response.statusCode === 200) {
        setAlert({
          type: 'success',
          message: `Configuration updated for ${editingProduct.name}`
        })
        setShowEditModal(false)
        setEditingProduct(null)
        loadDataProducts()
      } else {
        throw new Error(response.error || 'Failed to update configuration')
      }
    } catch (error) {
      console.error('Error updating configuration:', error)
      setAlert({
        type: 'error',
        message: `Failed to update configuration for ${editingProduct.name}`
      })
    }
  }

  const getCollectionStatusIndicator = (collectionStatus: string) => {
    switch (collectionStatus) {
      case 'processing':
        return <StatusIndicator type="in-progress">Processing</StatusIndicator>
      case 'idle':
        return <StatusIndicator type="success">Idle</StatusIndicator>
      default:
        return <StatusIndicator type="pending">Unknown</StatusIndicator>
    }
  }

  const getStatusIndicator = (status: string) => {
    switch (status) {
      case 'active':
        return <StatusIndicator type="success">Active</StatusIndicator>
      case 'inactive':
        return <StatusIndicator type="stopped">Inactive</StatusIndicator>
      case 'error':
        return <StatusIndicator type="error">Error</StatusIndicator>
      default:
        return <StatusIndicator type="pending">Unknown</StatusIndicator>
    }
  }

  const getTypeBadge = (type: string) => {
    switch (type) {
      case 'api':
        return <Badge color="blue">API</Badge>
      case 'scraping':
        return <Badge color="green">Web Scraping</Badge>
      default:
        return <Badge>Unknown</Badge>
    }
  }

  return (
    <SpaceBetween size="l">
      <Header variant="h1">Data Products</Header>

      {alert && (
        <Alert
          type={alert.type}
          dismissible
          onDismiss={() => setAlert(null)}
        >
          {alert.message}
        </Alert>
      )}

      <Container>
        <Cards
          cardDefinition={{
            header: item => item.name,
            sections: [
              {
                id: 'status',
                header: 'Status',
                content: item => getStatusIndicator(item.status)
              },
              {
                id: 'collectionStatus',
                header: 'Collection Status',
                content: item => getCollectionStatusIndicator(item.collectionStatus)
              },
              {
                id: 'type',
                header: 'Collection Method',
                content: item => getTypeBadge(item.type)
              },
              {
                id: 'locations',
                header: 'Locations',
                content: item => (
                  <Badge color="grey">{item.locationCount} locations</Badge>
                )
              },
              {
                id: 'lastCollection',
                header: 'Last Collection',
                content: item => item.lastCollection
              },
              {
                id: 'actions',
                header: 'Actions',
                content: item => canManage ? (
                  <SpaceBetween direction="horizontal" size="xs">
                    <Button 
                      variant="inline-link"
                      onClick={() => triggerCollection(item.id)}
                      disabled={item.collectionStatus === 'processing'}
                    >
                      Trigger Collection
                    </Button>
                    <Button 
                      variant="inline-link"
                      onClick={() => handleEditProduct(item)}
                    >
                      Edit
                    </Button>
                  </SpaceBetween>
                ) : null
              },
              {
                id: 'details',
                header: 'Configuration',
                content: item => (
                  <ColumnLayout columns={1} variant="text-grid">
                    <div>
                      <Box variant="awsui-key-label">Description</Box>
                      <div>{item.description}</div>
                    </div>
                    <div>
                      <Box variant="awsui-key-label">Collection Interval</Box>
                      <div>{item.collectInterval} minutes</div>
                    </div>
                    {item.endpoint && (
                      <div>
                        <Box variant="awsui-key-label">API Endpoint</Box>
                        <div style={{ wordBreak: 'break-all', fontSize: '0.9em' }}>
                          {item.endpoint}
                        </div>
                      </div>
                    )}
                    {item.scrapingTargets && (
                      <div>
                        <Box variant="awsui-key-label">Scraping Targets</Box>
                        <div>{item.scrapingTargets} websites</div>
                      </div>
                    )}
                  </ColumnLayout>
                )
              }
            ]
          }}
          cardsPerRow={[
            { cards: 1 },
            { minWidth: 700, cards: 2 },
            { minWidth: 1000, cards: 3 }
          ]}
          items={dataProducts}
          loading={loading}
          empty={
            <Box textAlign="center" color="inherit">
              <b>No data products</b>
              <Box variant="p" color="inherit">
                No data products are currently configured.
              </Box>
            </Box>
          }
          header={
            <Header
              counter={`(${dataProducts.length})`}
              description="Monitor and configure data collection sources"
              actions={
                <SpaceBetween direction="horizontal" size="xs">
                  <Button onClick={loadDataProducts} loading={loading}>
                    Refresh
                  </Button>
                </SpaceBetween>
              }
            >
              Data Products
            </Header>
          }
        />
      </Container>

      <Container header={<Header variant="h2">Collection Statistics</Header>}>
        <ColumnLayout columns={3} variant="text-grid">
          <div>
            <Box variant="awsui-key-label">Total Locations</Box>
            <Box variant="h2" color="text-status-info">
              {dataProducts.reduce((sum, product) => sum + product.locationCount, 0)}
            </Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Active Products</Box>
            <Box variant="h2" color="text-status-success">
              {dataProducts.filter(product => product.status === 'active').length}
            </Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Collection Methods</Box>
            <SpaceBetween direction="horizontal" size="xs">
              <Badge color="blue">
                {dataProducts.filter(p => p.type === 'api').length} API
              </Badge>
              <Badge color="green">
                {dataProducts.filter(p => p.type === 'scraping').length} Scraping
              </Badge>
            </SpaceBetween>
          </div>
        </ColumnLayout>
      </Container>

      <Modal
        visible={showEditModal}
        onDismiss={() => {
          setShowEditModal(false)
          setEditingProduct(null)
        }}
        header={editingProduct ? `Edit ${editingProduct.name}` : 'Edit Data Product'}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button 
                variant="link" 
                onClick={() => {
                  setShowEditModal(false)
                  setEditingProduct(null)
                }}
              >
                Cancel
              </Button>
              <Button 
                variant="primary" 
                onClick={handleSaveProduct}
              >
                Save Changes
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        {editingProduct && (
          <SpaceBetween direction="vertical" size="l">
            <FormField
              label="Status"
              description="Enable or disable data collection for this product"
            >
              <Toggle
                checked={editingProduct.status === 'active'}
                onChange={({ detail }) =>
                  setEditingProduct({
                    ...editingProduct,
                    status: detail.checked ? 'active' : 'inactive'
                  })
                }
              >
                {editingProduct.status === 'active' ? 'Enabled' : 'Disabled'}
              </Toggle>
            </FormField>

            <FormField
              label="Collection Interval (minutes)"
              description="How often to collect data from this source"
            >
              <Input
                type="number"
                value={String(editingProduct.collectInterval)}
                onChange={({ detail }) =>
                  setEditingProduct({
                    ...editingProduct,
                    collectInterval: parseInt(detail.value) || 60
                  })
                }
              />
            </FormField>

            <Box variant="p" color="text-body-secondary">
              <strong>Note:</strong> Changes will take effect on the next scheduled collection cycle.
            </Box>
          </SpaceBetween>
        )}
      </Modal>
    </SpaceBetween>
  )
}

export default DataProducts